import { z } from 'zod';
import { knownTotalQuerySchema } from './known-total';
import { loggedInUserMatchSchema } from './shadow-ai-logged-in-user';
/**
 * Alert severity levels for security scans
 */
export const alertSeveritySchema = z.enum([
    'low',
    'medium',
    'high',
    'critical',
]);
/**
 * Coverage status for IDE/platform clients.
 * - covered: Kirin is installed and actively monitoring
 * - supported: Kirin supports this tool but it is not actively monitored
 * - unknown: Kirin does not support this tool
 *
 * Order is used for sorting in backend SQL and frontend (covered first, then supported, then unknown).
 */
export const COVERAGE_STATUS_ORDER = [
    'covered',
    'supported',
    'unknown',
];
export const coverageStatusSchema = z.enum(COVERAGE_STATUS_ORDER);
/** Set of valid coverage statuses for handler validation. */
export const VALID_COVERAGE_STATUSES = new Set(COVERAGE_STATUS_ORDER);
/** Sort rank for coverage status (1 = covered, 2 = supported, 3 = unknown). Used in SQL ORDER BY and frontend sort. */
export function getCoverageOrderRank(status) {
    const index = COVERAGE_STATUS_ORDER.indexOf(status);
    return index >= 0 ? index + 1 : 999;
}
/** Display labels for coverage status (modal/table headers). */
export const COVERAGE_LABELS = {
    covered: 'Covered',
    supported: 'Supported (No Coverage)',
    unknown: 'Unknown',
};
/** Tooltip text for coverage status (badge hover). */
export const COVERAGE_TOOLTIPS = {
    covered: 'Kirin is actively covering this tool',
    supported: "Kirin supports this tool but data isn't being collected yet",
    unknown: '',
};
/**
 * Client activity - tracks an IDE client with its coverage status and optional activity timestamp.
 * Aggregated from user_client_activity (covered) and shadow_ai_tools (supported/unknown).
 */
export const clientActivitySchema = z.object({
    client: z.string(),
    coverageStatus: coverageStatusSchema,
    lastActiveAt: z.string().datetime().nullable(),
    displayName: z.string().nullish(),
    // KIR-2916: the account signed in to this client (from Shadow AI tool
    // attribution), and its match to the Kirin user. Present only when the
    // logged-in-user feature is enabled and an account was extracted — so this is
    // genuinely `.optional()` here (the posture handler omits it for unattributed
    // clients), unlike the running-now / tool-user schemas which always populate it.
    loggedInUserEmail: z.string().nullish(),
    loggedInUserMatch: loggedInUserMatchSchema.optional(),
});
/**
 * Per-platform MCP breakdown for expanded row view
 */
export const mcpPlatformBreakdownSchema = z.object({
    platform: z.string(), // Allows unknown platforms for forward compatibility
    mcpCount: z.number().int().min(0),
    // Default to [] for backward compatibility with older servers
    mcps: z.array(z.string()).optional().default([]),
});
/**
 * Alert filter options for the dropdown
 */
export const alertFilterSchema = z.enum(['all', 'with_alerts', 'no_alerts']);
/**
 * User security posture item - represents one user's security data
 */
export const userSecurityPostureItemSchema = z.object({
    userId: z.string().uuid(), // Internal UUID (from users.id, not descopeId)
    name: z.string().nullable(),
    email: z.string().nullable(),
    clients: z.array(clientActivitySchema).optional().default([]), // IDE clients used (sorted by lastActiveAt DESC)
    mcpCount: z.number().int().min(0), // Total MCP count
    mcpsByPlatform: z.array(mcpPlatformBreakdownSchema), // Per-platform MCP counts for expanded view
    alertCount: z.number().int().min(0), // Last 7 days
    maxAlertSeverity: alertSeveritySchema.nullable(),
    lastActiveAt: z.string().datetime().nullable(), // ISO datetime (most recent across all API keys)
    // KIR-3035: number of distinct endpoints (machines) this user has reported.
    // Optional for backward compatibility (flag off / older backend).
    endpointCount: z.number().int().min(0).optional(),
    // KIR-3911: the agent version of the *least up-to-date* live endpoint (one
    // whose last_seen is within the org's inventory-visibility window). Null when
    // the user has no live endpoint with a parseable numeric version. The
    // frontend colors it red when it trails the current released agent version.
    // Optional for backward compatibility (flag off / older backend).
    leastAgentVersion: z.string().nullable().optional(),
});
/**
 * Pagination info for the response
 */
export const userSecurityPosturePaginationSchema = z.object({
    page: z.number().int().min(1),
    limit: z.number().int().min(1).max(100),
    total: z.number().int().min(0),
    hasMore: z.boolean(),
});
/**
 * Full response for the user security posture endpoint
 */
export const userSecurityPostureResponseSchema = z.object({
    users: z.array(userSecurityPostureItemSchema),
    pagination: userSecurityPosturePaginationSchema,
    /** Human-readable list of data sources used for IDEs/Platforms (for UI display) */
    ideDataSources: z.array(z.string()).optional(),
});
/**
 * Query parameters for the user security posture endpoint
 */
export const userSecurityPostureQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    search: z.string().optional(),
    alertFilter: alertFilterSchema.default('all'),
});
/**
 * Query parameters for API client usage (optional, no defaults)
 */
export const userSecurityPostureParamsSchema = z.object({
    page: z.number().int().min(1).optional(),
    limit: z.number().int().min(1).max(100).optional(),
    search: z.string().optional(),
    alertFilter: alertFilterSchema.optional(),
});
/**
 * Default page size for posture management tabs (shared constant)
 */
export const DEFAULT_POSTURE_PAGE_SIZE = 50;
// ============================================================================
// DataTable-specific schemas
// ============================================================================
import { ColumnDataTypes, FilterOperators, TableResponseMetaSchema, createTableQuerySchema, } from './table';
import { paginationSchema, sortOrderSchema } from './helpers';
/**
 * Filterable fields for the User Security Posture DataTable.
 */
export const USER_SECURITY_POSTURE_FILTERABLE_FIELDS = {
    NAME: 'name',
    EMAIL: 'email',
    PLATFORMS_COVERED: 'platformsCovered',
    PLATFORMS_SUPPORTED: 'platformsSupported',
    MCP_COUNT: 'mcpCount',
    ALERT_COUNT: 'alertCount',
    LAST_ACTIVE_AT: 'lastActiveAt',
    // KIR-3035: filter users by endpoint (machine) NAME — the field key matches
    // the Endpoints column id. A user matches if ANY of their endpoints has one
    // of the selected names. (The column also sorts, by endpoint count.)
    ENDPOINTS: 'endpoints',
};
/**
 * Fields backed by a server-side filter-options endpoint (name, email, platforms).
 * mcpCount and alertCount are numeric; lastActiveAt uses date picker.
 */
export const USER_SECURITY_POSTURE_FILTER_OPTION_FIELDS = {
    NAME: 'name',
    EMAIL: 'email',
    PLATFORMS_COVERED: 'platformsCovered',
    PLATFORMS_SUPPORTED: 'platformsSupported',
    // KIR-3035: distinct endpoint names for the Endpoints filter dropdown.
    ENDPOINTS: 'endpoints',
};
export const userSecurityPostureFilterOptionFieldSchema = z.enum([
    'name',
    'email',
    'platformsCovered',
    'platformsSupported',
    'endpoints',
]);
/**
 * Column data types for User Security Posture DataTable fields.
 * Used by createTableQuerySchema to validate operator × datatype combinations.
 */
export const USER_SECURITY_POSTURE_COLUMN_DATA_TYPES = {
    name: ColumnDataTypes.ENUM,
    email: ColumnDataTypes.ENUM,
    platformsCovered: ColumnDataTypes.ENUM,
    platformsSupported: ColumnDataTypes.ENUM,
    mcpCount: ColumnDataTypes.NUMBER,
    alertCount: ColumnDataTypes.NUMBER,
    lastActiveAt: ColumnDataTypes.DATE,
    // KIR-3035: endpoint-name filter is a textual multi-select (enum).
    endpoints: ColumnDataTypes.ENUM,
};
// Narrow per column: lastActiveAt keeps range + value-less; covered/mcp/alert keep includes/excludes only (no value-less in their UI).
export const USER_SECURITY_POSTURE_OPERATOR_OVERRIDES = {
    [USER_SECURITY_POSTURE_FILTERABLE_FIELDS.LAST_ACTIVE_AT]: [
        FilterOperators.GREATER_THAN,
        FilterOperators.LESS_THAN,
        FilterOperators.BETWEEN,
        FilterOperators.IS_EMPTY,
        FilterOperators.IS_NOT_EMPTY,
    ],
    [USER_SECURITY_POSTURE_FILTERABLE_FIELDS.PLATFORMS_COVERED]: [
        FilterOperators.INCLUDES,
        FilterOperators.EXCLUDES,
    ],
    [USER_SECURITY_POSTURE_FILTERABLE_FIELDS.MCP_COUNT]: [
        FilterOperators.INCLUDES,
        FilterOperators.EXCLUDES,
    ],
    [USER_SECURITY_POSTURE_FILTERABLE_FIELDS.ALERT_COUNT]: [
        FilterOperators.INCLUDES,
        FilterOperators.EXCLUDES,
    ],
    // KIR-3035: endpoint-name filter — includes/excludes only (no value-less).
    [USER_SECURITY_POSTURE_FILTERABLE_FIELDS.ENDPOINTS]: [
        FilterOperators.INCLUDES,
        FilterOperators.EXCLUDES,
    ],
};
/**
 * Sort fields for the User Security Posture DataTable.
 */
export const userSecurityPostureDataTableSortFieldSchema = z.enum([
    'name',
    'email',
    'platformsCovered',
    'platformsSupported',
    'mcpCount',
    'alertCount',
    'lastActiveAt',
    // KIR-3035: sort by number of endpoints (machines) reported per user.
    'endpointCount',
]);
/**
 * Searchable fields for the User Security Posture DataTable.
 * These fields are searched when a search term is provided.
 */
export const USER_SECURITY_POSTURE_SEARCHABLE_FIELDS = {
    NAME: 'name',
    EMAIL: 'email',
    PLATFORMS_COVERED: 'platformsCovered',
    PLATFORMS_SUPPORTED: 'platformsSupported',
    MCP_COUNT: 'mcpCount',
    MCP_NAMES: 'mcpNames',
    ALERT_COUNT: 'alertCount',
    // KIR-3429: the account a user is logged into a tool with (e.g. the email
    // address shown in the "Logged-in accounts" breakdown).
    LOGGED_IN_USER: 'loggedInUser',
};
/**
 * Backend query schema for the User Security Posture DataTable.
 * Handles bracket-notation filter params from Fastify OR a pre-parsed filters array.
 */
const userSecurityPostureDataTableQueryBaseSchema = paginationSchema.extend({
    sortBy: userSecurityPostureDataTableSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    search: z.string().optional(),
    knownTotal: knownTotalQuerySchema,
});
export const userSecurityPostureDataTableQuerySchema = createTableQuerySchema(userSecurityPostureDataTableQueryBaseSchema, USER_SECURITY_POSTURE_COLUMN_DATA_TYPES, USER_SECURITY_POSTURE_OPERATOR_OVERRIDES);
/**
 * User Security Posture DataTable row item.
 * Extended from the base item with additional fields for DataTable rendering.
 */
export const userSecurityPostureDataTableItemSchema = userSecurityPostureItemSchema.extend({
    // Flattened fields for filtering
    platformsCovered: z.array(z.string()),
    platformsSupported: z.array(z.string()),
});
/**
 * Response schema for the User Security Posture DataTable endpoint.
 * Shape: { data: UserSecurityPostureDataTableItem[], pagination: {...}, meta: {...} }
 */
export const userSecurityPostureTableResponseSchema = z.object({
    data: z.array(userSecurityPostureDataTableItemSchema),
    pagination: z.object({
        page: z.number().int().min(1),
        limit: z.number().int().min(1),
        total: z.number().int().min(0),
        hasMore: z.boolean(),
    }),
    meta: TableResponseMetaSchema,
    /** Human-readable list of data sources used for IDEs/Platforms (for UI display) */
    ideDataSources: z.array(z.string()).optional(),
});
/**
 * Query schema for fetching User Security Posture filter options.
 */
export const userSecurityPostureFilterOptionsQuerySchema = z.object({
    field: userSecurityPostureFilterOptionFieldSchema,
    search: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(50).default(10),
    offset: z.coerce.number().int().min(0).default(0),
});
/**
 * Response schema for User Security Posture filter options.
 * Used by the filter dropdown to display available options.
 */
export const userSecurityPostureFilterOptionsResponseSchema = z.object({
    options: z.array(z.string()),
    total: z.number().int().min(0),
});
//# sourceMappingURL=user-security-posture.js.map